<template>
  <div>
    <exo-form hideClose :root="_self" :clear="false">
      <template v-slot:titleTooltip>
        <exo-help-dialog
          :color="'black--text'"
          :margin="'m-0'"
          title="Constellation Generation"
        >
          <tooltipConstellationGenerationMission />
        </exo-help-dialog>
      </template>
      <exo-input-name :root="_self" />
      <v-row>
        <v-col cols="12">
          <exo-card-form label="Generation">
            <exo-input-double :root="_self" name="altitude">
              <exo-help-dialog title="Altitude of operation">
                The altitude of operation defines the altitude of the generated
                constellation planes.
              </exo-help-dialog>
            </exo-input-double>
            <exo-input-ref-notparametric
              :root="_self"
              name="payload"
              type="payload"
              componentRoute="/analyticalconstellationpayload"
            >
              <exo-help-dialog title="Payload">
                The payload is the same for the entire constellation and must
                have a circular field of regard.
              </exo-help-dialog>
            </exo-input-ref-notparametric>
            <exo-input-ref-notparametric
              :root="_self"
              name="earthZoneOfInterest"
              type="earthZoneOfInterest"
              componentRoute="/analyticalconstellationearthzoneofinterest"
            >
            </exo-input-ref-notparametric>
            <exo-input-double :root="_self" name="maxAllowedPointRevisitTime">
              <exo-help-dialog title="Maximum revisit time">
                The value of the maximum revisit time defines the maximum
                allowed duration between two distinct visits of a point of
                interest.
              </exo-help-dialog>
            </exo-input-double>
          </exo-card-form>
          <exo-card-form label="Options">
            <exo-input-double :root="_self" name="maximumAllowedNumberOfPlanes">
              <exo-help-dialog title="Maximum allowed number of planes">
                The generation process will generate a constellation with a
                number of planes inferior or equal to the maximum value if it is
                possible. Since it is a constraint, the generated constellation
                might not be optimal in terms of the total number of
                spacecrafts.
              </exo-help-dialog>
            </exo-input-double>
            <exo-input-checkbox :root="_self" name="sunLightUsed">
              <exo-help-dialog
                margin="mb-5 ml-2"
                alignWithParametricField
                title="Use Sun light for payload visibility"
              >
                The sunlight can be taken into account in the performance
                metrics computation by defining an enlightenment condition.
              </exo-help-dialog>
            </exo-input-checkbox>
            <exo-input-double
              v-if="inputs.sunLightUsed"
              :root="_self"
              name="sunZenithAngle"
            >
              <exo-help-dialog title="Sun zenith angle">
                <tooltipSunZenithAngle />
              </exo-help-dialog>
            </exo-input-double>
            <exo-form-alert
              class="mb-2"
              type="info"
              v-if="displayPointsNeverEnlightenedWarning"
            >
              Some points of interest are never enlightened! The generated
              constellation may not cover those points.
            </exo-form-alert>
          </exo-card-form>
        </v-col>
      </v-row>
    </exo-form>
  </div>
</template>

<script>
import formMixin from '../../mixins/forms.js'
import formAnalyticalMixin from '../../mixins/analyticalFoms.js'
import constellationComponentsLoader from '../../components/Forms/analyticalConstellationsComponents.js'
import dataTools from '../../tools/data.js'
import missionTools from '../../store/mission.js'
import fieldsDescription from '../../tools/fieldsDescription.json'
import dataDescription from '../../tools/dataDescription.json'
import { mapGetters, mapActions } from 'vuex'
import tooltipConstellationGenerationMission from '../../components/Tooltips/ConstellationModule/tooltipConstellationGenerationMission'
import tooltipSunZenithAngle from '../../components/Tooltips/ConstellationModule/tooltipSunZenithAngle'

constellationComponentsLoader()

export default {
  name: 'ConstellationGenerationMission',

  mixins: [formMixin, formAnalyticalMixin],

  components: {
    tooltipConstellationGenerationMission,
    tooltipSunZenithAngle
  },

  async mounted() {
    this.$root.$on(
      'request-computation-constellation-generation',
      this.requestComputationConstellationGenerationHandler
    )
    this.$root.$on('copy-mission', this.copyMissionHandler)
    this.$root.$on('create-mission', this.createMissionHandler)
    this.$root.$on('current-mission-updated', this.currentMissionUpdatedHandler)
    await this.refreshReferences()
    this.refreshForm()
  },

  beforeDestroy() {
    this.$root.$off(
      'request-computation-constellation-generation',
      this.requestComputationConstellationGenerationHandler
    )
    this.$root.$off('copy-mission', this.copyMissionHandler)
    this.$root.$off('create-mission', this.createMissionHandler)
    this.$root.$off(
      'current-mission-updated',
      this.currentMissionUpdatedHandler
    )
  },

  computed: {
    ...mapGetters(['currentMission']),

    displayPointsNeverEnlightenedWarning() {
      if (
        this.inputs.sunLightUsed &&
        !(this.inputs.earthZoneOfInterest == null)
      ) {
        let maximumLatitude = this.computeMaximumAbsoluteLatitude()
        let maxEnlightenedArea =
          dataTools.deg2rad(this.inputs.sunZenithAngle) + 0.409045319
        return maximumLatitude > maxEnlightenedArea
      }
      return false
    }
  },

  data() {
    return {
      tableFields: [],

      enumPropertiesList: this.getEnumProperties(),

      modalRef: { content: [] },
      showModal: false,

      earthZoneOfInterestsNotParametrics: [],
      payloadsNotParametrics: [],

      inputs: {
        name: this.getDefaultValue('name'),
        description: this.getDefaultValue('description'),
        earthZoneOfInterest: null,
        layout: this.getDefaultValue('layout'),
        altitude: this.getDefaultValue('altitude'),
        maxAllowedPointRevisitTime: this.getDefaultValue(
          'maxAllowedPointRevisitTime'
        ),
        maximumAllowedNumberOfPlanes: this.getDefaultValue(
          'maximumAllowedNumberOfPlanes'
        ),
        sunLightUsed: this.getDefaultValue('sunLightUsed'),
        sunZenithAngle: this.getDefaultValue('sunZenithAngle'),
        payload: this.getDefaultValue('payload'),
        id: ''
      }
    }
  },

  methods: {
    ...mapActions(['setCurrentMission']),

    requestComputationConstellationGenerationHandler() {
      this.onSave()
    },
    copyMissionHandler(mission) {
      this.copyMission(mission)
    },
    createMissionHandler(mission) {
      this.createMission(mission)
    },
    currentMissionUpdatedHandler() {
      this.refreshForm()
    },

    computeMaximumAbsoluteLatitude() {
      let maximumAbsoluteLatitude = 0
      if (this.inputs.earthZoneOfInterest.isolatedPointsOfInterest != null) {
        for (let point of this.inputs.earthZoneOfInterest
          .isolatedPointsOfInterest) {
          let absoluteLatitude = Math.abs(point.latitude)
          if (absoluteLatitude > maximumAbsoluteLatitude) {
            maximumAbsoluteLatitude = absoluteLatitude
          }
        }
      }
      if (this.inputs.earthZoneOfInterest.earthMesh != null) {
        for (let earthMesh of this.inputs.earthZoneOfInterest.earthMesh) {
          let earthMeshMaximumAbsoluteLatitude
          if (earthMesh.rangeType === 'AUTOMATIC') {
            let maxLatitude = earthMesh.maximumLatitude
            let minLatitude = earthMesh.minimumLatitude
            earthMeshMaximumAbsoluteLatitude = Math.max(
              Math.abs(maxLatitude),
              Math.abs(minLatitude)
            )
          } else {
            let maxLatitude = earthMesh.maxLatitude
            let minLatitude = earthMesh.minLatitude
            earthMeshMaximumAbsoluteLatitude = Math.max(
              Math.abs(maxLatitude),
              Math.abs(minLatitude)
            )
          }
          if (earthMeshMaximumAbsoluteLatitude > maximumAbsoluteLatitude) {
            maximumAbsoluteLatitude = earthMeshMaximumAbsoluteLatitude
          }
        }
      }

      return maximumAbsoluteLatitude
    },

    refreshTable() {},

    async refreshReferences() {
      await this.refreshEarthZoneOfInterests()
      await this.refreshPayloads()
    },

    getRequiredPropertiesList(instance) {
      let objectType = this.getCurrentFormType()
      let missionType = this.getMissionType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        missionType,
        'required'
      )
    },

    getOptionalPropertiesList(instance) {
      let missionType = this.getMissionType(instance)
      let objectType = this.getCurrentFormType()
      return dataTools.getPropertiesList(
        fieldsDescription[objectType],
        missionType,
        'optional'
      )
    },

    getMissionType() {
      return null
    },

    copyMission(mission) {
      if (mission && mission.typeName === this.getCurrentFormType()) {
        this.copy(mission)
        this.sendFormToServer()
      }
    },

    refreshForm() {
      if (
        this.currentMission != null &&
        this.currentMission.typeName === this.getCurrentFormType()
      ) {
        if (this.currentMission.id !== this.inputs.id) {
          // different mission reload completely the form
          this.edit(this.currentMission, 0)
        } else {
          // reloading the current mission
          // only refresh the updated fields
          const fieldsToUpdate = [
            this.earthZoneOfInterestsNotParametrics,
            this.payloadsNotParametrics
          ]
          missionTools.updateAllReferences(this, fieldsToUpdate)
        }
      }
    },

    async refreshPayloads() {
      // TODO MFI automatic method for all of these getters
      let self = this
      await this.$http
        .get(this.getAPI(`payloaddefinitions`))
        .then((response) => {
          self.payloadsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of payloads. ',
            e,
            'notification',
            'error'
          )
        })
    },

    onSave: function (evt) {
      this.inputs.saving = true
      this.$http
        .put(
          this.getCurrentAPI() + '/' + this.inputs.id,
          this.createObjectFromForm()
        )
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('update-mission-list')
          this.$root.$emit('set-current-mission', response.data) // update fields and current mission
          // redirect to dashboard
          this.$router.push({ path: '/constellationgenerationdashboard' })
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'notification',
            'error'
          )
        })
    },

    async refreshEarthZoneOfInterests() {
      let self = this
      await this.$http
        .get(this.getAPI(`zoneofinterestdefinitions`))
        .then((response) => {
          self.earthZoneOfInterestsNotParametrics = response.data
            .map((obj) => ({ value: obj, text: obj.displayedName }))
            .sort((a, b) =>
              a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1
            )
        })
        .catch((e) => {
          self.displayMessage(
            'Error getting list of earth zones of interest. ',
            e,
            'notification',
            'error'
          )
        })
    },

    getRefList(param) {
      if (param === 'earthZoneOfInterest') {
        return this.earthZoneOfInterestsNotParametrics
      }
      if (param === 'payload') {
        return this.payloadsNotParametrics
      }
    },

    getCurrentFormTitle() {
      return 'Constellation Generation'
    },

    getCurrentFormType() {
      return 'ConstellationGenerationRequest'
    },

    createMission(mission) {
      if (mission.typeName === this.getCurrentFormType()) {
        this.clearForm()
        this.type = mission.typeName
        this.sendFormToServer()
      }
    },

    sendFormToServer() {
      this.inputs.saving = true
      this.$http
        .post(this.getCurrentAPI(), this.createObjectFromForm())
        .then((response) => {
          this.inputs.saving = false
          this.$root.$emit('set-current-mission', response.data)
        })
        .catch((e) => {
          this.inputs.saving = false
          this.displayMessage(
            'Error creating ' + this.inputs.type + '. ',
            e,
            'AnalyticalMissionErrorDisplay',
            'error'
          )
        })
    },

    addFieldsListToBaseObject(
      baseObject,
      fieldsList,
      parentObject = this.inputs
    ) {
      for (let field of fieldsList) {
        if (this.getPropertiesDescription()[field] != null) {
          let fieldType = this.getPropertiesDescription()[field].type
          if (
            fieldType === 'enum' ||
            fieldType === 'boolean' ||
            fieldType === 'date'
          ) {
            baseObject[field] = parentObject[field]
          } else if (fieldsDescription[fieldType] != null) {
            baseObject[field] = parentObject[field]
          } else {
            baseObject[field] = dataTools.convertGui2si(
              this.getFieldType(field),
              parentObject[field]
            )
          }
        }
      }
      return baseObject
    },

    clearLocalFields() {
      for (let field in this.getPropertiesDescription()) {
        if (this.inputs.hasOwnProperty(field)) {
          this.inputs[field] = this.getDefaultValue(field)
          let fieldType = this.getPropertiesDescription()[field].type
          if (dataDescription[fieldType] != null) {
            this.checkParametricValue(this.inputs, field)
          } else if (fieldsDescription[fieldType] != null) {
            this.checkParametricReference(this.inputs, field)
          }
        }
      }
      this.inputs.subMissions = []
    },

    copyLocalFields(item) {
      this.inputs.id = item.id
      this.copyFieldsToBaseObject(item, this.inputs)
    },

    addLocalFieldsFromForm(baseObject, fieldsList) {
      baseObject = this.addFieldsListToBaseObject(baseObject, fieldsList)
      baseObject.computationRequested = true
      return baseObject
    },

    copyFieldsToBaseObject(item, baseObject) {
      for (let field in this.getPropertiesDescription()) {
        if (item.hasOwnProperty(field)) {
          let fieldType = this.getPropertiesDescription()[field].type
          if (dataDescription[fieldType] !== undefined) {
            // parametric value
            baseObject[field] = dataTools.convertSi2gui(
              this.getFieldType(field),
              item[field]
            )
            this.checkParametricValue(baseObject, field)
          } else {
            baseObject[field] = item[field]
            if (fieldsDescription[fieldType] != null) {
              this.checkParametricReference(baseObject, field)
            }
          }
        }
      }
    }
  }
}
</script>
